import React, { FC, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { Box, FormControl, FormErrorMessage, FormLabel, Input, InputGroup, InputRightElement } from '@chakra-ui/react';
import DatePicker from 'react-date-picker';
import { Value } from 'react-date-picker/dist/cjs/shared/types';

import 'react-calendar/dist/Calendar.css';
import { API_FEAT_DATE_FORMAT, USER_DATE_FORMAT } from '@app/utils/constants';

import { Dropdown } from './dropdown';

type DateFieldProps = {
  id: string;
  label: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  disabled?: boolean;
  value?: string;
  onChange?: (value: string) => void;
};

const DateField: FC<DateFieldProps> = (props) => {
  const { 
    id,
    label,
    value, 
    onChange, 
    required,
    disabled,
    error,
    helperText,
  } = props;
  const [dateValue, onChangeValue] = useState<Value>(value ? moment(value).toDate() : undefined);

  useEffect(() => {
    onChange(dateValue ? moment(dateValue as Date).format(API_FEAT_DATE_FORMAT) : undefined);
  }, [dateValue]);

  const portalContainerRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  return (<InputGroup zIndex={1}>
    <FormControl 
      variant="floating" 
      id={id} 
      isRequired={required}
      isInvalid={error}
    >
      <Box
        w='100%'
        minH='var(--input-height)'
        display='flex'
        zIndex={1}
        __css={{
          '& .react-date-picker': { display: 'flex', width: '100%', alignItems: 'center'},
          '& .react-date-picker__wrapper': { 
            display: 'flex', width: '100%', alignItems: 'center', gap: 0,
            border: '1px solid',
            borderRadius: 'var(--input-border-radius)',
            paddingInlineStart: 4, paddingInlineEnd: 2, paddingY: 2,
          },
          '& .react-date-picker__inputGroup': { flex: 1 },
          '& .react-date-picker__button': { width: '24px', height: '24px', },
          '& .react-date-picker__button:hover':   { backgroundColor: 'var(--chakra-colors-gray-100)', borderRadius: '50%'},
          '& .react-date-picker__button svg': { width: '12px', marginX: 'auto' },
        }}
        ref={fieldRef}
      >
        {!disabled ? <DatePicker  
          onChange={onChangeValue} 
          value={dateValue} 
          format='dd.MM.yyyy' 
          isOpen={open}
          portalContainer={portalContainerRef.current} 
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          disabled={disabled}
        /> : <Input value={moment(value).format(USER_DATE_FORMAT) || ''} />}
      </Box>
      <Input 
        position="absolute"
        opacity={0}
        placeholder={label}
        value={dateValue?.toString() || ''}
        contentEditable={false}
        isReadOnly
        height={0}
      />
      <FormLabel textColor="dark.300">{label}</FormLabel>
      {error && <FormErrorMessage>{helperText}</FormErrorMessage>}
    </FormControl>
    {!disabled && <Dropdown 
      fieldRef={fieldRef}
      open={open}
      setOpen={setOpen}
      autoWidth
    >
      <Box 
        ref={portalContainerRef}
        __css={{
          '& .react-date-picker__calendar': { display: 'none'},
          '& .react-date-picker__calendar--open': { display: 'block' },
          '& .react-calendar': { backgroundColor: 'white', borderRadius: 'md', overflow: 'hidden', borderColor: 'gray.200 !important' },
          '& .react-calendar__tile--now': { backgroundColor: 'light.500' },
          '& .react-calendar__tile--now:hover': { backgroundColor: 'light.600' },
        }}
      />

    </Dropdown>}
  </InputGroup>);
};

export default DateField;
